import React, { useState, useEffect, useCallback } from 'react';
import { CreateCardRequest, Language } from '../services/poiService';
import { getActiveLanguages } from '../services/api';
import SimpleAudioRecorder from './SimpleAudioRecorder';
import SimpleImageUploader from './SimpleImageUploader';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { checkUrlAccessibility, debounce } from '../utils/urlValidator';
import { Check, X, Loader2 } from 'lucide-react';

interface CardFormProps {
  poiId: string; // POI UUID
  poiName: string;
  onSubmit: (data: CreateCardRequest) => Promise<void>;
  onDuplicate?: (data: CreateCardRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  isDuplicating?: boolean;
  initialData?: Partial<CreateCardRequest> & { language_code?: string };
}

const CardForm: React.FC<CardFormProps> = ({ poiId, poiName, onSubmit, onDuplicate, onCancel, isLoading = false, isDuplicating = false, initialData }) => {
  const [formData, setFormData] = useState<CreateCardRequest>({
    poi_id: poiId,
    category: initialData?.category || '',
    language_code: initialData?.language_code || undefined,
    title: initialData?.title || '',
    subtitle: initialData?.subtitle || '',
    text: initialData?.text || '',
    audio_url: initialData?.audio_url || '',
    video_url: initialData?.video_url || '',
    image_url: initialData?.image_url || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [audioInputMode, setAudioInputMode] = useState<'url' | 'record'>('record');
  const [imageInputMode, setImageInputMode] = useState<'url' | 'upload'>('url');
  const [isChildMode, setIsChildMode] = useState(initialData?.category === 'child-mode');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  
  // URL validation states
  const [urlValidation, setUrlValidation] = useState({
    image_url: { isChecking: false, isAccessible: false, checked: false },
    audio_url: { isChecking: false, isAccessible: false, checked: false },
    video_url: { isChecking: false, isAccessible: false, checked: false }
  });

  // Fetch active languages on component mount
  useEffect(() => {
    const fetchLanguages = async () => {
      setLoadingLanguages(true);
      try {
        const response = await getActiveLanguages();
        setLanguages(response.data || []);
      } catch (error) {
        console.error('Error fetching languages:', error);
        setLanguages([]);
      } finally {
        setLoadingLanguages(false);
      }
    };

    fetchLanguages();
  }, []);

  // Function to validate URL accessibility
  const validateUrl = useCallback(async (url: string, field: 'image_url' | 'audio_url' | 'video_url') => {
    if (!url || url.trim() === '') {
      setUrlValidation(prev => ({
        ...prev,
        [field]: { isChecking: false, isAccessible: false, checked: false }
      }));
      return;
    }

    // Set checking state
    setUrlValidation(prev => ({
      ...prev,
      [field]: { ...prev[field], isChecking: true }
    }));

    try {
      const type = field === 'image_url' ? 'image' : field === 'audio_url' ? 'audio' : 'video';
      const result = await checkUrlAccessibility(url, type);
      
      setUrlValidation(prev => ({
        ...prev,
        [field]: { isChecking: false, isAccessible: result.isAccessible, checked: true }
      }));
    } catch (error) {
      setUrlValidation(prev => ({
        ...prev,
        [field]: { isChecking: false, isAccessible: false, checked: true }
      }));
    }
  }, []);

  // Debounced URL validation (wait 1 second after user stops typing)
  const debouncedValidateUrl = useCallback(
    debounce((url: string, field: 'image_url' | 'audio_url' | 'video_url') => {
      validateUrl(url, field);
    }, 1000),
    [validateUrl]
  );

  // Configure ReactQuill with basic formatting only (no hyperlinks)
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'list', 'bullet'
  ];

  // Helper function to strip HTML tags for character counting
  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Category is not required anymore
    
    // Validate allowed content combinations
    const hasText = formData.text?.trim() && stripHtmlTags(formData.text).trim();
    const hasAudio = formData.audio_url?.trim();
    const hasVideo = formData.video_url?.trim();
    const hasImage = formData.image_url?.trim();
    
    const mediaCount = (hasAudio ? 1 : 0) + (hasVideo ? 1 : 0) + (hasImage ? 1 : 0);
    
    // Check for allowed combinations: text only, text+video, text+image, text+audio+image
    if (!hasText && mediaCount === 0) {
      newErrors.content = 'At least one content field must be provided. Allowed combinations: text only, text + YouTube video URL, text + image URL, or text + audio URL + image URL.';
    } else if (!hasText && mediaCount > 0) {
      newErrors.content = 'Text content is required when adding media URLs.';
    } else if (hasAudio && !hasImage) {
      newErrors.content = 'Audio content requires both text and image. Please add an image URL.';
    } else if (hasVideo && (hasAudio || hasImage)) {
      newErrors.content = 'Video cannot be combined with other media types. Choose either: text + video URL, or text + image URL, or text + audio URL + image URL.';
    } else if (mediaCount > 2) {
      newErrors.content = 'Too many media types. Choose either: text + video URL, or text + image URL, or text + audio URL + image URL.';
    }
    
    // Validate text length using stripped HTML content
    if (formData.text) {
      const strippedText = stripHtmlTags(formData.text);
      if (strippedText.length > 2000) {
        newErrors.text = 'Text content must be less than 2000 characters';
      }
    }
    
    // Basic URL validation for media fields
    const urlPattern = /^https?:\/\/.+/;
    const videoPattern = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/|tiktok\.com\/@[\w.-]+\/video\/\d+|vm\.tiktok\.com\/[\w]+)/;
    
    if (formData.audio_url && !urlPattern.test(formData.audio_url)) {
      newErrors.audio_url = 'Audio URL must be a valid HTTP/HTTPS URL';
    }
    if (formData.video_url) {
      if (!urlPattern.test(formData.video_url)) {
        newErrors.video_url = 'Video URL must be a valid HTTP/HTTPS URL';
      } else if (!videoPattern.test(formData.video_url)) {
        newErrors.video_url = 'Video URL must be a YouTube URL (youtube.com, youtu.be, youtube.com/shorts) or TikTok URL (tiktok.com/@username/video/ID, vm.tiktok.com/ID)';
      }
    }
    if (formData.image_url && !urlPattern.test(formData.image_url)) {
      newErrors.image_url = 'Image URL must be a valid HTTP/HTTPS URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await onSubmit(buildCleanData());
    } catch (error) {
      console.error('Error submitting Card form:', error);
    }
  };

  const buildCleanData = (): CreateCardRequest => {
    const cleanData: CreateCardRequest = {
      poi_id: formData.poi_id
    };
    if (isChildMode) {
      cleanData.category = 'child-mode';
    }
    if (formData.language_code) cleanData.language_code = formData.language_code;
    if (formData.title?.trim()) cleanData.title = formData.title.trim();
    if (formData.subtitle?.trim()) cleanData.subtitle = formData.subtitle.trim();
    if (formData.text?.trim()) cleanData.text = formData.text.trim();
    if (formData.audio_url?.trim()) cleanData.audio_url = formData.audio_url.trim();
    if (formData.video_url?.trim()) cleanData.video_url = formData.video_url.trim();
    if (formData.image_url?.trim()) cleanData.image_url = formData.image_url.trim();
    return cleanData;
  };

  const handleDuplicate = async () => {
    if (!onDuplicate) return;
    try {
      await onDuplicate(buildCleanData());
    } catch (error) {
      console.error('Error duplicating Card:', error);
    }
  };

  const handleChildModeToggle = () => {
    setIsChildMode(!isChildMode);
  };

  const handleInputChange = (field: keyof CreateCardRequest, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (field !== 'category' && errors.content) {
      setErrors(prev => ({ ...prev, content: '' }));
    }
    
    // Trigger URL validation for URL fields
    if (field === 'image_url' || field === 'audio_url' || field === 'video_url') {
      if (typeof value === 'string') {
        debouncedValidateUrl(value, field);
      }
    }
  };

  const handleAudioUploaded = (audioUrl: string) => {
    handleInputChange('audio_url', audioUrl);
  };

  const handleImageUploaded = (imageUrl: string) => {
    handleInputChange('image_url', imageUrl);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {initialData ? 'Edit Card' : 'Add Card'} for {poiName}
      </h3>
      
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 rounded-lg p-3 mb-4">
        <p className="text-blue-800 text-sm">
          <strong>Allowed content combinations:</strong> text only, text + YouTube video URL, text + image URL, or text + recorded/URL audio + image URL. Note: Audio requires both text and image.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">Child Mode</span>
            <button
              type="button"
              onClick={handleChildModeToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isChildMode ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
              disabled={isLoading}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
                  isChildMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {isChildMode && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 rounded-md p-3 mb-4">
              <p className="text-blue-800 text-sm">
                <strong>Child Mode:</strong> This card will be categorized as child-friendly content.
              </p>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="card-language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Language
          </label>
          {loadingLanguages ? (
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 dark:border-primary-400"></div>
              <span>Loading languages...</span>
            </div>
          ) : (
            <select
              id="card-language"
              value={formData.language_code || ''}
              onChange={(e) => handleInputChange('language_code', e.target.value || undefined)}
              className={`form-input ${errors.language_code ? 'border-red-500' : ''}`}
              disabled={isLoading}
            >
              <option value="">Select a language (optional)</option>
              {languages.map((language) => (
                <option key={language.id} value={language.code}>
                  {language.native_name} ({language.name})
                </option>
              ))}
            </select>
          )}
          {errors.language_code && <p className="text-red-500 text-sm mt-1">{errors.language_code}</p>}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Select the content language for this card. This helps users find content in their preferred language.
          </p>
        </div>

        <div>
          <label htmlFor="card-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Title (Optional)
          </label>
          <input
            id="card-title"
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className={`form-input ${errors.title ? 'border-red-500' : ''}`}
            placeholder="Enter a title for this card..."
            disabled={isLoading}
            maxLength={255}
          />
          {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {formData.title ? formData.title.length : 0}/255 characters
          </p>
        </div>

        <div>
          <label htmlFor="card-subtitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subtitle (Optional)
          </label>
          <input
            id="card-subtitle"
            type="text"
            value={formData.subtitle}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            className={`form-input ${errors.subtitle ? 'border-red-500' : ''}`}
            placeholder="Enter a subtitle for this card..."
            disabled={isLoading}
            maxLength={255}
          />
          {errors.subtitle && <p className="text-red-500 text-sm mt-1">{errors.subtitle}</p>}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {formData.subtitle ? formData.subtitle.length : 0}/255 characters
          </p>
        </div>

        <div>
          <label htmlFor="card-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Text Content
          </label>
          <div className={`${errors.text ? 'border-red-500 border rounded-md' : ''}`}>
            <ReactQuill
              value={formData.text}
              onChange={(value) => handleInputChange('text', value)}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Describe this location or share interesting information..."
              readOnly={isLoading}
              theme="snow"
              style={{ 
                backgroundColor: isLoading ? '#f9fafb' : 'white',
                minHeight: '120px'
              }}
            />
          </div>
          {errors.text && <p className="text-red-500 text-sm mt-1">{errors.text}</p>}
          <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
            {formData.text ? stripHtmlTags(formData.text).length : 0}/2000 characters
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Image Content
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setImageInputMode('url');
                }}
                className={`px-3 py-1 text-xs rounded ${
                  imageInputMode === 'url'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                }`}
                disabled={isLoading}
              >
                🔗 URL
              </button>
              <button
                type="button"
                onClick={() => {
                  setImageInputMode('upload');
                }}
                className={`px-3 py-1 text-xs rounded ${
                  imageInputMode === 'upload'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                }`}
                disabled={isLoading}
              >
                📁 Upload
              </button>
            </div>
          </div>
          
          {imageInputMode === 'upload' ? (
            <SimpleImageUploader
              title="Card"
              onImageUploaded={handleImageUploaded}
              existingImageUrl={formData.image_url}
            />
          ) : (
            <div className="relative">
              <input
                id="card-image-url"
                type="url"
                value={formData.image_url}
                onChange={(e) => handleInputChange('image_url', e.target.value)}
                className={`form-input pr-10 ${errors.image_url ? 'border-red-500' : ''}`}
                placeholder="https://example.com/image.jpg"
                disabled={isLoading}
              />
              {formData.image_url && imageInputMode === 'url' && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {urlValidation.image_url.isChecking ? (
                    <Loader2 className="h-5 w-5 text-gray-400 dark:text-gray-500 animate-spin" />
                  ) : urlValidation.image_url.checked ? (
                    urlValidation.image_url.isAccessible ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : (
                      <X className="h-5 w-5 text-red-500" />
                    )
                  ) : null}
                </div>
              )}
            </div>
          )}
          {errors.image_url && <p className="text-red-500 text-sm mt-1">{errors.image_url}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Audio Content
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setAudioInputMode('record');
                }}
                className={`px-3 py-1 text-xs rounded ${
                  audioInputMode === 'record'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                }`}
                disabled={isLoading}
              >
                🎤 Record
              </button>
              <button
                type="button"
                onClick={() => {
                  setAudioInputMode('url');
                }}
                className={`px-3 py-1 text-xs rounded ${
                  audioInputMode === 'url'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
                }`}
                disabled={isLoading}
              >
                🔗 URL
              </button>
            </div>
          </div>
          
          {audioInputMode === 'record' ? (
            <SimpleAudioRecorder
              title="Card"
              onAudioUploaded={handleAudioUploaded}
              existingAudioUrl={formData.audio_url}
            />
          ) : (
            <div className="relative">
              <input
                id="card-audio-url"
                type="url"
                value={formData.audio_url}
                onChange={(e) => handleInputChange('audio_url', e.target.value)}
                className={`form-input pr-10 ${errors.audio_url ? 'border-red-500' : ''}`}
                placeholder="https://example.com/audio.mp3"
                disabled={isLoading}
              />
              {formData.audio_url && audioInputMode === 'url' && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {urlValidation.audio_url.isChecking ? (
                    <Loader2 className="h-5 w-5 text-gray-400 dark:text-gray-500 animate-spin" />
                  ) : urlValidation.audio_url.checked ? (
                    urlValidation.audio_url.isAccessible ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : (
                      <X className="h-5 w-5 text-red-500" />
                    )
                  ) : null}
                </div>
              )}
            </div>
          )}
          {errors.audio_url && <p className="text-red-500 text-sm mt-1">{errors.audio_url}</p>}
        </div>

        <div>
          <label htmlFor="card-video-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Video URL (YouTube/TikTok)
          </label>
          <div className="relative">
            <input
              id="card-video-url"
              type="url"
              value={formData.video_url}
              onChange={(e) => handleInputChange('video_url', e.target.value)}
              className={`form-input pr-10 ${errors.video_url ? 'border-red-500' : ''}`}
              placeholder="https://youtube.com/watch?v=..., https://youtu.be/..., https://tiktok.com/@user/video/..., or https://vm.tiktok.com/..."
              disabled={isLoading}
            />
            {formData.video_url && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                {urlValidation.video_url.isChecking ? (
                  <Loader2 className="h-5 w-5 text-gray-400 dark:text-gray-500 animate-spin" />
                ) : urlValidation.video_url.checked ? (
                  urlValidation.video_url.isAccessible ? (
                    <Check className="h-5 w-5 text-green-500" />
                  ) : (
                    <X className="h-5 w-5 text-red-500" />
                  )
                ) : null}
              </div>
            )}
          </div>
          {errors.video_url && <p className="text-red-500 text-sm mt-1">{errors.video_url}</p>}
        </div>

        {errors.content && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-600 text-sm">{errors.content}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isLoading || isDuplicating}
          >
            Cancel
          </button>
          {initialData && onDuplicate && (
            <button
              type="button"
              onClick={handleDuplicate}
              className="btn-secondary"
              disabled={isLoading || isDuplicating}
            >
              {isDuplicating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2 inline-block"></div>
                  Duplicating...
                </>
              ) : (
                'Duplicate Card'
              )}
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading || isDuplicating}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {initialData ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              initialData ? 'Update Card' : 'Create Card'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CardForm;